import { definePreset } from "@primeng/themes";
import Aura from "@primeng/themes/aura";
import { BrandConfig, BrandPreset } from "../brand-config";

export const enoraPreset: BrandPreset = definePreset(Aura, {
  name: "Enora",
  primitive: {
    enora: {
      50: "#DDFFD6",
      100: "#DDFFD6",
      200: "#B0F5A2",
      300: "#B0F5A2",
      400: "#B0F5A2", // ENORA brand color
      500: "#56CC5E",
      600: "#56CC5E",
      700: "#0C7A1F",
      800: "#0C7A1F",
      900: "#0A3311",
      950: "#0A3311",
    },
  },
  semantic: {
    primary: {
      color: "{surface.700}",
      contrastColor: "{surface.0}",
      hoverColor: "{surface.800}",
      activeColor: "{surface.950}",
    },
    highlight: {
      background: "{enora.400}",
      color: "{surface.950}",
    },
    colorScheme: {
      light: {
        primary: {
          color: "{surface.700}",
          contrastColor: "{surface.900}",
          hoverColor: "{surface.800}",
          activeColor: "{surface.950}",
        },
        highlight: {
          background: "{enora.50}",
          focusBackground: "{enora.400}",
          color: "{surface.700}",
          focusColor: "{surface.950}",
        },
      },
      dark: {
        primary: {
          color: "{surface.0}",
          contrastColor: "{surface.700}",
          activeColor: "{surface.50}",
          borderColor: "{surface.500}",
          hoverColor: "{surface.300}",
        },
        highlight: {
          background: "{enora.900}",
          focusBackground: "{enora.800}",
          color: "{surface.300}",
          focusColor: "{surface.100}",
        },
        content: {
          background: "{surface.900}",
          borderColor: "{surface.800}",
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              color: "{surface.950}",
              background: "{enora.400}",
              borderColor: "{enora.400}",
              hoverBackground: "{enora.500}",
              hoverColor: "{surface.950}",
              hoverBorderColor: "{enora.500}",
              activeColor: "{enora.500}",
            },
            outlined: {
              primary: {
                color: "{surface.950}",
                borderColor: "{surface.950}",
                hoverColor: "{surface.950}",
                hoverBackground: "{enora.50}",
                activeBackground: "{enora.400}",
              },
              danger: {
                borderColor: "{red.500}",
              },
            },
            text: {
              primary: {
                color: "{surface.950}",
                hoverBackground: "{enora.50}",
              },
            },
          },
        },
        dark: {
          root: {
            primary: {
              color: "{surface.950}",
              background: "{enora.400}",
              borderColor: "{enora.400}",
              hoverBackground: "{enora.500}",
              hoverColor: "{surface.950}",
              hoverBorderColor: "{enora.500}",
              activeBackground: "{enora.50}",
              activeBorderColor: "{enora.50}",
            },
            outlined: {
              primary: {
                color: "{surface.0}",
                borderColor: "{surface.0}",
                hoverColor: "{surface.0}",
                hoverBackground: "{enora.50}",
                activeBackground: "{enora.50}",
              },
            },
            text: {
              primary: {
                color: "{surface.0}",
                hoverColor: "{surface.100}",
              },
            },
          },
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          icon: {
            checked: {
              color: "#ffffff",
              hoverColor: "{enora.50}",
            },
          },
        },
        // Dark mode: green accent box (matching the dark primary button) + dark checkmark.
        // Without this, the box falls back to {primary.color} which is white in Enora's dark scheme.
        dark: {
          root: {
            checkedBackground: "{enora.400}",
            checkedHoverBackground: "{enora.500}",
            checkedBorderColor: "{enora.400}",
            checkedHoverBorderColor: "{enora.500}",
            checkedFocusBorderColor: "{enora.400}",
          },
          icon: {
            checked: {
              color: "{surface.950}",
              hoverColor: "{surface.950}",
            },
          },
        },
      },
    },
    radiobutton: {
      colorScheme: {
        light: {
          checked: {
            color: "#ffffff",
          },
          icon: {
            checked: {
              color: "#ffffff",
              hoverColor: "{enora.50}",
            },
          },
        },
        // Dark mode: green accent (matching the dark primary button) + dark indicator dot.
        dark: {
          root: {
            checkedBackground: "{enora.400}",
            checkedHoverBackground: "{enora.500}",
            checkedBorderColor: "{enora.400}",
            checkedHoverBorderColor: "{enora.500}",
            checkedFocusBorderColor: "{enora.400}",
          },
          icon: {
            checked: {
              color: "{surface.950}",
              hoverColor: "{surface.950}",
            },
          },
        },
      },
    },
    datepicker: {
      date: {
        selectedColor: "{surface.950}",
      },
    },
  },
});

export const enoraBrand: BrandConfig = {
  brandId: "enora",
  companyName: "Enora",
  productName: "Connect",
  primeNgPreset: enoraPreset,
  pageBackground: { light: "#F8F8F6", dark: "#0F0F0E" }, // surface-50 (light) / surface-950 (dark)
  shadowColor: "var(--p-surface-200)",
  internalCard: {
    light: { background: "var(--p-primary-50)", borderColor: "var(--p-primary-700)" },
    dark: { background: "var(--p-primary-900)", borderColor: "var(--p-primary-700)" },
  },
  internalTag: {
    light: { background: "var(--p-surface-0)", color: "var(--p-surface-900)" },
    dark: { background: "var(--p-surface-0)", color: "var(--p-surface-900)" },
  },
  font: {
    family: "Geist",
    // Geist ships only upright weights — no italic variants
    fontFaces: [
      {
        weight: 100,
        sources: [{ url: "/brands/enora/fonts/Geist-Thin.ttf", format: "truetype" }],
      },
      {
        weight: 200,
        sources: [{ url: "/brands/enora/fonts/Geist-ExtraLight.ttf", format: "truetype" }],
      },
      {
        weight: 300,
        sources: [{ url: "/brands/enora/fonts/Geist-Light.ttf", format: "truetype" }],
      },
      {
        weight: 400,
        sources: [{ url: "/brands/enora/fonts/Geist-Regular.ttf", format: "truetype" }],
      },
      {
        weight: 500,
        sources: [{ url: "/brands/enora/fonts/Geist-Medium.ttf", format: "truetype" }],
      },
      {
        weight: 600,
        sources: [{ url: "/brands/enora/fonts/Geist-SemiBold.ttf", format: "truetype" }],
      },
      {
        weight: 700,
        sources: [{ url: "/brands/enora/fonts/Geist-Bold.ttf", format: "truetype" }],
      },
      {
        weight: 800,
        sources: [{ url: "/brands/enora/fonts/Geist-ExtraBold.ttf", format: "truetype" }],
      },
      {
        weight: 900,
        sources: [{ url: "/brands/enora/fonts/Geist-Black.ttf", format: "truetype" }],
      },
    ],
  },
  surfacePalette: {
    0: "#FFFFFF",
    50: "#F8F8F6",
    100: "#f0f0eb",
    200: "#DCDCD5",
    300: "#C8C8C0",
    400: "#A4A49D",
    500: "#80807A",
    600: "#676761",
    700: "#4D4D48",
    800: "#43433D",
    900: "#2E2E27",
    950: "#0F0F0E",
  },
  logos: {
    horizontalLogoLightMode: "brands/enora/enora-logotype-light-mode.svg",
    horizontalLogoDarkMode: "brands/enora/enora-logotype-dark-mode.svg",
    iconLogoLightMode: "brands/enora/enora-avatar-logo.svg",
    iconLogoDarkMode: "brands/enora/enora-avatar-logo.svg",
  },
  favicons: {
    ico: "brands/enora/favicon.ico",
    png16: "brands/enora/favicon-16x16.png",
    png32: "brands/enora/favicon-32x32.png",
    appleTouchIcon: "brands/enora/apple-touch-icon.png",
    androidChrome192: "brands/enora/android-chrome-192x192.png",
    androidChrome512: "brands/enora/android-chrome-512x512.png",
  },
  supportEmail: "tuki.connect@rantalainen.fi",
  legalEmail: "legal@rantalainen.fi",
  gdprUrls: {
    en: "https://www.rantalainen.fi/en/gdpr/",
    fi: "https://www.rantalainen.fi/gdpr/",
    sv: "https://www.rantalainen.fi/sv/gdpr/",
    no: "https://www.rantalainen.fi/en/gdpr/",
  },
};
